import { resetMixpanel } from '@proposify/frontend-observability';
import { generateNewAccessToken } from '../authorization/tokenUtils';
import { logoutUser } from '../authorization/handleLogout';
import { isTransientRefreshFailure } from '../authorization/tokenUtils';
import { trackError } from '@proposify/frontend-observability';

let inFlightRefresh: Promise<{ expires_in: number }> | null = null;

export const refreshTokenFlow = (): Promise<{ expires_in: number }> => {
  if (inFlightRefresh) {
    return inFlightRefresh;
  }

  const refreshPromise = (async () => {
    try {
      const { expires_in } = await generateNewAccessToken();
      // Backend sets frontend_token via Set-Cookie on the refresh response itself;
      // no need to re-set it here. Doing so from JS would overwrite an httpOnly
      // cookie with a JS-writable one, since cookie identity is name+domain+path
      // only (httpOnly isn't part of it) — a security regression, not just dead code.
      return { expires_in };
    } catch (e) {
      trackError('RefreshTokenFlow Error', {
        message: e instanceof Error ? e.message : String(e),
        stack: e instanceof Error ? e.stack : undefined,
        errorFrom: 'proposify-frontend',
        cause: 'refreshTokenFlow API call failed',
      });
      if (isTransientRefreshFailure(e)) {
        throw e;
      }
      logoutUser();
      resetMixpanel();
      throw e;
    }
  })();

  // Reset via .finally() on the outer promise, not a finally block inside the
  // async IIFE above: when that IIFE resolves without ever awaiting (the
  // cookie-still-valid fast path), its own finally runs before this
  // assignment line completes, so the assignment overwrites the reset and
  // wedges inFlightRefresh non-null forever. .finally() on refreshPromise
  // always defers to a microtask, so it can't race the assignment.
  inFlightRefresh = refreshPromise.finally(() => {
    inFlightRefresh = null;
  });

  return inFlightRefresh;
};
